<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import RecentDonations from './RecentDonations.svelte';
  import RecentPayouts from './RecentPayouts.svelte';
  import { 
    CharityDashboardService,
    type RecentDonation,
    type DonationPayout
  } from '../../../lib/api/charityServices';
  import {
    setCharityPendingConfirmation,
  } from '../../../stores/charity-pending-confirmation.store';

  type MetricLink = { href: string; aria: string };
  
  // Dashboard metrics
  let totalValue = 0;
  let incomingDonationsValue = 0;
  let pendingConfirmationValue = 0;
  let pendingConfirmationCount = 0;

  $: showPendingBanner =
    !dashboardStatsLoading &&
    (pendingConfirmationCount > 0 || pendingConfirmationValue > 0);
  $: pendingBannerLabel =
    pendingConfirmationCount === 1
      ? '1 payment awaiting your confirmation'
      : pendingConfirmationCount > 1
        ? `${pendingConfirmationCount} payments awaiting your confirmation`
        : 'Payments awaiting your confirmation';

  // Loading states
  let loading = true;
  let error: string | null = null;
  let dashboardStatsLoading = true;
  let dashboardStatsError: string | null = null;

  // Data states
  let recentPendingConfirmation: DonationPayout[] = [];
  let recentIncomingDonations: RecentDonation[] = [];
  let recentDonationsReceived: DonationPayout[] = [];

  type DashboardTab = 'pending-confirmation' | 'incoming' | 'received';
  let activeTab: DashboardTab = 'pending-confirmation';

  // Dashboard metrics — order matches tabs: Pending Confirmation, Incoming, Received
  let dashboardMetrics = [
    {
      id: 'pending-confirmation',
      link: { href: '/pending-confirmation', aria: 'Confirm received payouts' } as MetricLink,
      label: 'Pending Confirmation',
      value: '$0.00',
      icon: 'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z',
      bgColor: 'bg-purple-custom',
      iconColor: 'text-white',
      dotColor: '#8B5CF6',
      viewbox: '0 0 24 24'
    },
    {
      id: 'incoming',
      link: { href: '/donation-requests', aria: 'View incoming donations' } as MetricLink,
      label: 'Incoming Donations',
      value: '$0.00',
      icon: 'M21 15.7486C21 19.8873 17.2499 21.7477 12 21.7477C6.75012 21.7477 3 19.8873 3 15.7779C3 11.2786 5.25035 8.99899 9.74965 6.7194L8.15658 3.7519C8.06153 3.60326 8.0086 3.43162 8.00344 3.25527C7.99828 3.07891 8.04107 2.90447 8.12726 2.75052C8.21345 2.59658 8.3398 2.46892 8.49285 2.38115C8.64589 2.29337 8.81989 2.24878 8.99629 2.25213H15.356C15.5214 2.25636 15.6829 2.30259 15.8255 2.38647C15.968 2.47035 16.0869 2.58912 16.1708 2.7316C16.2548 2.87409 16.3012 3.0356 16.3055 3.20093C16.3099 3.36627 16.2721 3.52999 16.1957 3.6767L14.2503 6.7194C18.7483 8.96836 21 11.248 21 15.7486Z',
      bgColor: 'bg-red-custom',
      iconColor: 'text-white',
      dotColor: '#FC6C6B',
      viewbox: '0 0 24 24'
    },
    {
      id: 'value',
      link: { href: '/payouts', aria: 'View donations received' } as MetricLink,
      label: 'Donations Received',
      value: '$0',
      icon: 'M21 15.7486C21 19.8873 17.2499 21.7477 12 21.7477C6.75012 21.7477 3 19.8873 3 15.7779C3 11.2786 5.25035 8.99899 9.74965 6.7194L8.15658 3.7519C8.06153 3.60326 8.0086 3.43162 8.00344 3.25527C7.99828 3.07891 8.04107 2.90447 8.12726 2.75052C8.21345 2.59658 8.3398 2.46892 8.49285 2.38115C8.64589 2.29337 8.81989 2.24878 8.99629 2.25213H15.356C15.5214 2.25636 15.6829 2.30259 15.8255 2.38647C15.968 2.47035 16.0869 2.58912 16.1708 2.7316C16.2548 2.87409 16.3012 3.0356 16.3055 3.20093C16.3099 3.36627 16.2721 3.52999 16.1957 3.6767L14.2503 6.7194C18.7483 8.96836 21 11.248 21 15.7486ZM12.8703 9.05748C12.8703 8.82665 12.7786 8.60528 12.6154 8.44206C12.4522 8.27884 12.2308 8.18714 12 8.18714C11.7692 8.18714 11.5478 8.27884 11.3846 8.44206C11.2214 8.60528 11.1297 8.82665 11.1297 9.05748V9.93061C10.5398 9.95333 9.97844 10.1906 9.55113 10.598C9.12382 11.0053 8.85991 11.5546 8.80896 12.1427C8.75801 12.7309 8.92353 13.3174 9.27443 13.7921C9.62533 14.2668 10.1375 14.5971 10.7147 14.721L12.7659 15.1693C12.9761 15.2146 13.1622 15.3361 13.2881 15.5104C13.4141 15.6847 13.4711 15.8995 13.4481 16.1133C13.4251 16.3271 13.3237 16.5249 13.1635 16.6684C13.0034 16.8119 12.7957 16.891 12.5807 16.8905H11.4193C11.2393 16.8908 11.0636 16.8353 10.9164 16.7315C10.7693 16.6277 10.6579 16.4809 10.5977 16.3112C10.5625 16.1999 10.5053 16.0968 10.4295 16.008C10.3537 15.9192 10.2608 15.8466 10.1563 15.7945C10.0518 15.7424 9.9379 15.7118 9.82137 15.7047C9.70484 15.6975 9.58805 15.7138 9.47796 15.7527C9.36787 15.7915 9.26673 15.8522 9.18056 15.931C9.09438 16.0097 9.02494 16.105 8.97636 16.2112C8.92778 16.3174 8.90105 16.4322 8.89778 16.5489C8.8945 16.6656 8.91474 16.7818 8.95729 16.8905C9.12141 17.3527 9.41207 17.7596 9.79616 18.0646C10.1802 18.3697 10.6423 18.5607 11.1297 18.6159V19.5015C11.1297 19.7324 11.2214 19.9537 11.3846 20.117C11.5478 20.2802 11.7692 20.3719 12 20.3719C12.2308 20.3719 12.4522 20.2802 12.6154 20.117C12.7786 19.9537 12.8703 19.7324 12.8703 19.5015V18.6159C13.4848 18.546 14.0546 18.2605 14.4783 17.81C14.9019 17.3595 15.1521 16.7733 15.1842 16.1557C15.2162 15.5381 15.0282 14.9292 14.6535 14.4372C14.2787 13.9453 13.7416 13.6022 13.1377 13.4691L11.0865 13.0207C10.9201 12.9866 10.7723 12.8917 10.6722 12.7545C10.572 12.6173 10.5267 12.4476 10.5449 12.2787C10.5632 12.1099 10.6438 11.9539 10.771 11.8413C10.8982 11.7287 11.0629 11.6676 11.2327 11.6699H12.5807C12.761 11.6686 12.9372 11.7237 13.0845 11.8276C13.2318 11.9315 13.343 12.0789 13.4023 12.2492C13.4833 12.4614 13.644 12.6336 13.8502 12.7289C14.0564 12.8242 14.2917 12.8351 14.5059 12.7593C14.72 12.6835 14.896 12.527 14.9963 12.3232C15.0966 12.1194 15.1133 11.8844 15.0427 11.6685C14.8798 11.2057 14.5895 10.7983 14.2051 10.4933C13.8208 10.1883 13.3581 9.99804 12.8703 9.94453V9.05748Z',
      bgColor: 'bg-value-custom',
      iconColor: 'text-white',
      dotColor: '#FCB201',
      viewbox: '0 0 24 24'
    },
  ];

  // Load dashboard data from API
  async function loadDashboardData() {
    dashboardStatsLoading = true;
    loading = true;
    dashboardStatsError = null;
    error = null;

    try {
      const result = await CharityDashboardService.getDashboardStats();
      
      if (result.success && result.stats) {
        totalValue = result.stats.totalValueCents / 100;
        incomingDonationsValue = (result.stats.incomingDonationsValueCents || 0) / 100;
        pendingConfirmationValue = (result.stats.pendingConfirmationValueCents || 0) / 100;
        pendingConfirmationCount = result.stats.pendingConfirmationCount ?? 0;
        setCharityPendingConfirmation(
          pendingConfirmationCount,
          result.stats.pendingConfirmationValueCents || 0
        );

        recentPendingConfirmation = result.recentPendingConfirmation || [];
        recentIncomingDonations = result.recentIncomingDonations || [];
        recentDonationsReceived = result.recentDonationsReceived || [];

        updateDashboardMetrics();
      } else {
        dashboardStatsError = result.error || 'Failed to load dashboard data';
        error = 'Failed to load dashboard data';
      }
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      dashboardStatsError = 'An error occurred while loading dashboard data';
      error = 'An error occurred while loading dashboard data';
    } finally {
      dashboardStatsLoading = false;
      loading = false;
    }
  }

  function updateDashboardMetrics() {
    dashboardMetrics = dashboardMetrics.map(metric => {
      if (metric.id === 'value') {
        return { ...metric, value: formatCurrency(totalValue * 100) };
      } else if (metric.id === 'incoming') {
        return { ...metric, value: formatCurrency(incomingDonationsValue * 100) };
      } else if (metric.id === 'pending-confirmation') {
        return { ...metric, value: formatCurrency(pendingConfirmationValue * 100) };
      }
      return metric;
    });
  }

  function formatCurrency(cents: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }

  onMount(() => {
    loadDashboardData();
  });
</script>

<div class="charity-dashboard">
  <div class="dashboard-header">
    <p class="subtitle">Welcome to your charity dashboard</p>
  </div>

  {#if showPendingBanner}
    <div class="pending-confirmation-banner" role="status" aria-live="polite">
      <div class="banner-copy">
        <span class="banner-eyebrow">Action required</span>
        <p class="banner-title">{pendingBannerLabel}</p>
        <p class="banner-text">
          {formatCurrency(pendingConfirmationValue * 100)} has been paid to your charity.
          Please confirm once the funds are in your bank account.
        </p>
      </div>
      <button
        type="button"
        class="banner-action"
        on:click={() => push('/pending-confirmation')}
      >
        Review payments
      </button>
    </div>
  {/if}

  <!-- Top Metrics Cards -->
  <div class="metrics-grid">
    {#if dashboardStatsLoading}
      {#each dashboardMetrics as metric}
        <div class="metric-card loading">
          <div class="loading-shimmer"></div>
        </div>
      {/each}
    {:else}
      {#each dashboardMetrics as metric}
        <a
          class="metric-card metric-card-link"
          href={metric.link.href}
          aria-label="{metric.label}: {metric.value}. {metric.link.aria}."
          on:click|preventDefault={() => push(metric.link.href)}
          on:keydown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              push(metric.link.href);
            }
          }}
        >
          <div class="metric-content">
            <div class="metric-left">
              <div class="metric-label-container">
                <span class="metric-dot" style="background-color: {metric.dotColor}"></span>
                <span class="metric-label">{metric.label}</span>
              </div>
              <div class="metric-value">{metric.value}</div>
            </div>
            <div class="metric-icon {metric.bgColor}" aria-hidden="true">
              <svg class="icon {metric.iconColor}" viewBox={metric.viewbox} fill="currentColor">
                <path d={metric.icon} />
              </svg>
            </div>
          </div>
        </a>
      {/each}
    {/if}
  </div>

  <!-- Tabs and Content -->
  <div class="dashboard-content">
    <div class="tabs-container">
      <div class="tabs">
        <button 
          class="tab-button" 
          class:active={activeTab === 'pending-confirmation'} 
          on:click={() => activeTab = 'pending-confirmation'}
        >
          Pending Confirmation
        </button>
        <button 
          class="tab-button" 
          class:active={activeTab === 'incoming'} 
          on:click={() => activeTab = 'incoming'}
        >
          Incoming Donations
        </button>
        <button 
          class="tab-button" 
          class:active={activeTab === 'received'} 
          on:click={() => activeTab = 'received'}
        >
          Donations Received
        </button>
      </div>
    </div>

    {#if activeTab === 'pending-confirmation'}
      <RecentPayouts
        recentPayouts={recentPendingConfirmation}
        viewAllHref="#/pending-confirmation"
        {loading}
        {error}
      />
    {:else if activeTab === 'incoming'}
      <RecentDonations
        donations={recentIncomingDonations}
        {loading}
        {error}
      />
    {:else}
      <RecentPayouts
        recentPayouts={recentDonationsReceived}
        viewAllHref="#/payouts"
        showConfirmationStatus={false}
        {loading}
        {error}
      />
    {/if}
  </div>
</div>

<style>
  .charity-dashboard {
    padding: 2rem 0;
    max-width: 1900px;
    margin: 0 auto;
  }

  .dashboard-header {
    margin-bottom: 2rem;
    padding: 0 2rem;
  }

  .pending-confirmation-banner {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
    margin: 0 2rem 2rem;
    padding: 1.25rem 1.5rem;
    border: 1px solid #c4b5fd;
    border-radius: 12px;
    background: linear-gradient(135deg, #f5f3ff 0%, #fff 72%);
    box-shadow: 0 4px 14px rgba(139, 92, 246, 0.12);
  }

  .banner-copy {
    flex: 1;
    min-width: 0;
  }

  .banner-eyebrow {
    display: inline-block;
    margin-bottom: 0.35rem;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #6d28d9;
  }

  .banner-title {
    margin: 0 0 0.35rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: #4c1d95;
  }

  .banner-text {
    margin: 0;
    font-size: 0.875rem;
    color: #5b21b6;
    line-height: 1.5;
    max-width: 40rem;
  }

  .banner-action {
    flex-shrink: 0;
    padding: 0.625rem 1.25rem;
    border: none;
    border-radius: var(--border-radius-button, 10px);
    background: var(--color-primary, #ffb400);
    color: #111827;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease, transform 0.1s ease;
    white-space: nowrap;
  }

  .banner-action:hover {
    background: var(--color-primary-hover, #f0a800);
  }

  .banner-action:active {
    transform: translateY(1px);
  }

  .banner-action:focus {
    outline: none;
  }

  .banner-action:focus-visible {
    outline: 2px solid var(--color-primary, #ffb400);
    outline-offset: 2px;
  }

  .subtitle {
    color: #6b7280;
    font-size: 1rem;
    margin: 0;
  }

  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
    margin-bottom: 3rem;
    padding: 0 2rem;
  }

  .metric-card {
    background: white;
    border-radius: 12px;
    padding: 1rem 1.25rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
    border: 1px solid #f3f4f6;
    transition: transform 0.2s, box-shadow 0.2s;
    min-height: 90px;
    position: relative;
    overflow: hidden;
  }

  a.metric-card-link {
    text-decoration: none;
    color: inherit;
    cursor: pointer;
    display: block;
  }

  a.metric-card-link:focus {
    outline: none;
  }

  a.metric-card-link:focus-visible {
    outline: 2px solid var(--color-primary, #ffb400);
    outline-offset: 2px;
  }

  .metric-card.loading {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .loading-shimmer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.5) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
  }

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  .metric-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }

  .metric-content {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    height: 100%;
  }

  .metric-left {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .metric-label-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .metric-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .metric-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    margin: 0;
  }

  .metric-value {
    font-size: 2rem;
    font-weight: 700;
    color: #111827;
    line-height: 1;
    margin-top: 0.75rem;
  }

  .metric-icon {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .icon {
    width: 1.5rem;
    height: 1.5rem;
  }

  .dashboard-content {
    padding: 0 2rem;
  }

  /* Tab styles */
  .tabs-container {
    margin-bottom: 1.5rem;
  }

  .tabs {
    display: flex;
    gap: 1rem;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 1px;
  }

  .tab-button {
    padding: 0.75rem 1.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #6b7280;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .tab-button:hover {
    color: #111827;
  }

  .tab-button.active {
    color: #f0b429;
    border-bottom-color: #f0b429;
  }

  /* Responsive styles */
  @media (max-width: 768px) {
    .charity-dashboard {
      padding: 0;
      max-width: 100%;
    }

    .dashboard-header,
    .dashboard-content,
    .metrics-grid,
    .pending-confirmation-banner {
      padding-left: 0;
      padding-right: 0;
    }

    .pending-confirmation-banner {
      margin-left: 0;
      margin-right: 0;
      flex-direction: column;
      align-items: stretch;
    }

    .banner-action {
      width: 100%;
    }

    .metrics-grid {
      grid-template-columns: 1fr;
      gap: 0.75rem;
      margin-bottom: 1.5rem;
    }

    .metric-card {
      padding: 0.875rem 1rem;
      min-height: auto;
    }

    .metric-value {
      font-size: 1.5rem;
      margin-top: 0.5rem;
    }

    .metric-icon {
      width: 2.25rem;
      height: 2.25rem;
    }

    .tabs {
      gap: 0;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    .tab-button {
      flex: 1;
      text-align: center;
      padding: 0.625rem 0.75rem;
      font-size: 0.8125rem;
      white-space: nowrap;
    }
  }

  @media (max-width: 480px) {
    .tab-button {
      padding: 0.5rem;
    }
  }

  /* Add background color utility classes */
  :global(.bg-value-custom) {
    background-color: #FCB201;
  }

  :global(.bg-purple-custom) {
    background-color: #8B5CF6;
  }

  :global(.bg-red-custom) {
    background-color: #FC6C6B;
  }

  :global(.text-white) {
    color: white;
  }
</style> 